const LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
const LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';

export type LeafletMap = {
  remove: () => void;
  fitBounds: (bounds: unknown, options?: { padding: [number, number]; maxZoom?: number }) => void;
  panTo: (latlng: [number, number], options?: { animate?: boolean; duration?: number }) => void;
  getZoom: () => number;
};

export type LeafletMarker = {
  addTo: (map: LeafletMap) => LeafletMarker;
  bindPopup: (html: string) => LeafletMarker;
  setIcon: (icon: unknown) => LeafletMarker;
  getLatLng: () => { lat: number; lng: number };
  openPopup: () => void;
};

export type LeafletDivIcon = unknown;

export type LeafletLib = {
  map: (el: HTMLElement, options: { scrollWheelZoom?: boolean }) => LeafletMap;
  tileLayer: (url: string, options: { attribution: string }) => { addTo: (map: LeafletMap) => void };
  marker: (latlng: [number, number], options?: { icon?: LeafletDivIcon }) => LeafletMarker;
  latLngBounds: (latlngs: [number, number][]) => unknown;
  divIcon: (options: {
    className?: string;
    html?: string;
    iconSize?: [number, number];
    iconAnchor?: [number, number];
  }) => LeafletDivIcon;
};

export function loadLeaflet(): Promise<LeafletLib> {
  const w = window as Window & { L?: LeafletLib; __leafletLoadPromise?: Promise<LeafletLib> };
  if (w.L) return Promise.resolve(w.L);
  if (w.__leafletLoadPromise) return w.__leafletLoadPromise;

  w.__leafletLoadPromise = new Promise((resolve, reject) => {
    if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = LEAFLET_CSS;
      document.head.appendChild(link);
    }

    const existing = document.querySelector(`script[src="${LEAFLET_JS}"]`) as HTMLScriptElement | null;
    const onReady = () => {
      if (w.L) resolve(w.L);
      else reject(new Error('Leaflet failed to load'));
    };

    if (existing) {
      if (w.L) onReady();
      else {
        existing.addEventListener('load', onReady);
        existing.addEventListener('error', () => reject(new Error('Leaflet script error')));
      }
      return;
    }

    const script = document.createElement('script');
    script.src = LEAFLET_JS;
    script.async = true;
    script.onload = onReady;
    script.onerror = () => reject(new Error('Leaflet script failed'));
    document.body.appendChild(script);
  });

  return w.__leafletLoadPromise;
}

export function createDevelopmentMapPinIcon(L: LeafletLib, isActive: boolean): LeafletDivIcon {
  return L.divIcon({
    className: 'developments-map-marker-icon',
    html: `<span class="developments-map-pin${isActive ? ' developments-map-pin--active' : ''}" aria-hidden="true"></span>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}
